import type { components } from '@multaqa/api-client'
import { apiErrorFromResponse } from '@multaqa/api-client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { apiOrigin, fetchWithSessionActivity } from '@/lib/api'
import { meQuery } from '@/lib/me'

export { reportSearchFrom } from './report-search'

/**
 * The typed boundary to the report endpoints under `/api/accounting/reports`
 * (plans/07 stream E, FR-ACC-091..095).
 *
 * ## The envelope
 *
 * `report.show`'s 200 body is a union of six JsonResource envelopes —
 * `{ data: ProfitAndLossResource } | { data: BalanceSheetResource } | …` —
 * so `response.data` needs ONE peel (`unwrap` reads `.data`), and the
 * generic picks the member the caller asked for. This is the same two-envelope
 * rule the rest of the app documents: openapi-fetch gives `{ data, error }`,
 * the HTTP body is itself `{ "data": … }`.
 *
 * ## Calls that cannot go through the typed client
 *
 * - **Report filters** may send repeated `cost_center_id[]` values; the
 *   generated client serializes arrays without the brackets Laravel expects.
 * - **Drill-down** reads `?ids[]=…`, but the contract documents no query
 *   parameters for it (`query?: never`), and openapi-fetch would serialise an
 *   array as repeated `ids=a&ids=b` — which PHP's `parse_str` collapses to the
 *   last value. The backend wants the literal `ids[]=` form.
 * - **Export** returns a file, not JSON. `downloadFile` in `src/lib` does the
 *   fetch and hands the bytes to the browser.
 *
 * The report and drill-down calls use a hand-built `fetch`, the same sanctioned
 * escape hatch as `features/hr-attachments` (header + credentials by hand).
 *
 * Money is a decimal string at full scale (ADR-0012). What arrives here is
 * what the screens pass to `formatMoney`; nothing in this file parses an
 * amount.
 */

export type ReportName =
  | 'profit-and-loss'
  | 'balance-sheet'
  | 'general-ledger'
  | 'cost-by-name'
  | 'aging'
  | 'tax'
  | 'cash-flow'
  | 'ar-aging'
  | 'ar-aging-detail'
  | 'customer-balance'
  | 'customer-balance-detail'
  | 'open-invoices'
  | 'invoice-list'
  | 'invoice-payment'
  | 'collections'
  | 'statement-invoice'
  | 'terms-list'
  | 'ap-aging'
  | 'ap-aging-detail'
  | 'vendor-balance'
  | 'vendor-balance-detail'
  | 'unpaid-bills'
  | 'bill-payment'
  | 'bill-payment-list'
  | 'vendor-expenses'
  | 'journal'
  | 'general-ledger-list'
  | 'transaction-detail-by-account'
  | 'transaction-list-by-date'
  | 'transaction-list-by-customer'
  | 'transaction-list-by-vendor'
  | 'transaction-list-with-splits'
  | 'account-list'
  | 'payment-method-list'
  | 'deposit-detail'
  | 'check-detail'
  | 'recent-transactions'
  | 'invalid-journal-transactions'
  | 'reconciliation-reports'
  | 'sales-tax-liability'
  | 'taxable-sales'
  | 'taxable-sales-detail'

export type ReportRow = components['schemas']['ReportRowResource']
export type ProfitAndLoss = components['schemas']['ProfitAndLossResource']
export type BalanceSheet = components['schemas']['BalanceSheetResource']
export type GeneralLedger = components['schemas']['GeneralLedgerResource']
export type AgingReport = components['schemas']['AgingReportResource']
export type TaxReport = components['schemas']['TaxReportResource']
export type CashFlow = components['schemas']['CashFlowResource']
export type DrillDownLine = components['schemas']['DrillDownLineResource']
export type Account = components['schemas']['AccountResource']
export type SavedReport = components['schemas']['SavedReportResource']
export type SavedReportInput = components['schemas']['SavedReportRequest']

/** The filter surface every report accepts (mirrors `ReportFiltersRequest`). */
export interface ReportQuery {
  from?: string
  to?: string
  as_at?: string
  cost_center_id?: string
  cost_center_ids?: string[]
  analysis_tag_ids?: string[]
  journal_ids?: string[]
  group_by?: 'cost_center' | 'analysis_tag'
  compare_prior?: boolean
  side?: 'receivable' | 'payable'
  /** Aging only: which date the buckets count from; the API defaults to due_date. */
  age_by?: 'due_date' | 'document_date'
  basis?: 'accrual' | 'cash'
  columns_by?: string
  compare_to?: string
  filters?: string
  show_zero_rows?: boolean
  party_type?: 'customer' | 'supplier' | 'employee'
  party_id?: string
  /** General ledger only: the account whose running balance is shown. */
  account_id?: string
  /** General ledger only: a source type, or `manual`; hides lines, never changes a balance. */
  doc_type?: string
}

/**
 * The same surface as `ReportQuery`, as URL search params — what the route
 * shells validate and the screens write back through `onSearchChange`.
 */
export type ReportSearch = ReportQuery

/**
 * Validate raw URL search params into a `ReportSearch`, dropping anything
 * that is not a well-formed filter. Shared by the six report route shells so
 * each one does not restate the same narrowing (the invoices route is the
 * precedent for keeping this in the shell; six copies is the point where the
 * shared form wins).
 */
/** Today, in the browser's own timezone, as `YYYY-MM-DD`. */
export function todayIso(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/** The active company and its base currency, from `/me`. */
export interface ReportContext {
  companyId: string | null
  currency: string
}

export function useReportContext(): ReportContext {
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null
  const currency =
    (me?.memberships ?? []).find((membership) => membership.company_id === companyId)
      ?.company_base_currency ?? ''
  return { companyId, currency }
}

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

/**
 * One report, typed by the caller. The query object is part of the key, so
 * changing any filter refetches and each filter combination is cached
 * separately.
 */
export function useReport<T>(
  companyId: string | null,
  report: ReportName,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, Error>({
    queryKey: ['companies', companyId, 'accounting', 'reports', report, query] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<T> => {
      if (!companyId) {
        throw new Error('Cannot load a report without an active company.')
      }
      const params = new URLSearchParams()
      if (query.from !== undefined) params.set('from', query.from)
      if (query.to !== undefined) params.set('to', query.to)
      if (query.as_at !== undefined) params.set('as_at', query.as_at)
      if (query.cost_center_id !== undefined) params.set('cost_center_id', query.cost_center_id)
      for (const id of query.cost_center_ids ?? []) params.append('cost_center_id[]', id)
      for (const id of query.analysis_tag_ids ?? []) params.append('analysis_tag_ids[]', id)
      for (const id of query.journal_ids ?? []) params.append('journal_ids[]', id)
      if (query.group_by !== undefined) params.set('group_by', query.group_by)
      if (query.compare_prior === true) params.set('compare_prior', '1')
      if (query.side !== undefined) params.set('side', query.side)
      if (query.age_by !== undefined) params.set('age_by', query.age_by)
      if (query.party_type !== undefined) params.set('party_type', query.party_type)
      if (query.party_id !== undefined) params.set('party_id', query.party_id)
      if (query.account_id !== undefined) params.set('account_id', query.account_id)
      if (query.doc_type !== undefined) params.set('doc_type', query.doc_type)
      const search = params.toString()
      const apiPrefix = apiOrigin === '' ? '' : `${apiOrigin}/api`
      const response = await fetchWithSessionActivity(
        `${apiPrefix}/accounting/reports/${report}${search ? `?${search}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-Company-Id': companyId },
        },
      )
      if (!response.ok) throw await apiErrorFromResponse(response)
      return unwrap<T>((await response.json()) as { data?: unknown }, report)
    },
  })
}

export function useProfitAndLoss(
  companyId: string | null,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<ProfitAndLoss, Error>, 'queryKey' | 'queryFn'>,
) {
  return useReport<ProfitAndLoss>(companyId, 'profit-and-loss', query, options)
}

export function useBalanceSheet(
  companyId: string | null,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<BalanceSheet, Error>, 'queryKey' | 'queryFn'>,
) {
  return useReport<BalanceSheet>(companyId, 'balance-sheet', query, options)
}

export function useGeneralLedger(
  companyId: string | null,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<GeneralLedger, Error>, 'queryKey' | 'queryFn'>,
) {
  return useReport<GeneralLedger>(companyId, 'general-ledger', query, options)
}

export function useAgingReport(
  companyId: string | null,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<AgingReport, Error>, 'queryKey' | 'queryFn'>,
) {
  return useReport<AgingReport>(companyId, 'aging', query, options)
}

export function useTaxReport(
  companyId: string | null,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<TaxReport, Error>, 'queryKey' | 'queryFn'>,
) {
  return useReport<TaxReport>(companyId, 'tax', query, options)
}

export function useCashFlow(
  companyId: string | null,
  query: ReportQuery,
  options?: Omit<UseQueryOptions<CashFlow, Error>, 'queryKey' | 'queryFn'>,
) {
  return useReport<CashFlow>(companyId, 'cash-flow', query, options)
}

export {
  type ExportFormat,
  exportReport,
  reportExportUrl,
  saveReport,
  updateSavedReport,
  useAccounts,
  useDrillDownLines,
  useSavedReports,
} from './api-extras'
